import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { FirebaseError } from 'firebase/app';

/**
 * Custom hook to automatically capture and report errors to Sentry
 * Handles network errors, Firebase errors, and other application errors
 */
export const useSentryErrorCapture = () => {
  useEffect(() => {
    // Capture unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'unhandledRejection');
        scope.setLevel('error');
        
        // Add context for different error types
        if (error instanceof FirebaseError) {
          scope.setTag('errorSource', 'firebase');
          scope.setContext('firebase', {
            code: error.code,
            message: error.message,
            customData: error.customData
          });
        } else if (error instanceof TypeError && error.message.includes('fetch')) {
          scope.setTag('errorSource', 'network');
          scope.setContext('network', {
            message: error.message,
            stack: error.stack
          });
        } else {
          scope.setTag('errorSource', 'unknown');
        }
        
        Sentry.captureException(error);
      });
      
      // Log to console in development
      if (import.meta.env.DEV) {
        console.error('Unhandled promise rejection:', error);
      }
    };

    // Capture global JavaScript errors
    const handleError = (event: ErrorEvent) => {
      Sentry.withScope((scope) => {
        scope.setTag('errorType', 'globalError');
        scope.setLevel('error');
        scope.setContext('errorEvent', {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          message: event.message
        });
        
        Sentry.captureException(event.error || new Error(event.message));
      });
      
      if (import.meta.env.DEV) {
        console.error('Global error:', event.error || event.message);
      }
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup function
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);
};

/**
 * Utility function to manually capture Firebase errors with additional context
 */
export const captureFirebaseError = (error: FirebaseError, context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    scope.setTag('errorSource', 'firebase');
    scope.setTag('firebaseCode', error.code);
    scope.setLevel('error');
    
    scope.setContext('firebase', {
      code: error.code,
      message: error.message,
      customData: error.customData,
      ...context
    });
    
    Sentry.captureException(error);
  });
};

/**
 * Utility function to manually capture network errors with additional context
 */
export const captureNetworkError = (error: Error, context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    scope.setTag('errorSource', 'network');
    scope.setLevel('error');
    
    scope.setContext('network', {
      message: error.message,
      stack: error.stack,
      ...context
    });
    
    Sentry.captureException(error);
  });
};

/**
 * Utility function to capture API errors with request/response context
 */
export const captureApiError = (
  error: Error, 
  requestContext: {
    url?: string;
    method?: string;
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
  }
) => {
  Sentry.withScope((scope) => {
    scope.setTag('errorSource', 'api');
    scope.setTag('apiStatus', requestContext.status?.toString() || 'unknown');
    scope.setLevel('error');
    
    scope.setContext('api', {
      url: requestContext.url,
      method: requestContext.method,
      status: requestContext.status,
      statusText: requestContext.statusText,
      headers: requestContext.headers,
      errorMessage: error.message
    });
    
    Sentry.captureException(error);
  });
};

/**
 * Utility function to add user context to Sentry
 */
export const setSentryUser = (user: {
  id: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username
  });
};

/**
 * Utility function to clear user context from Sentry
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Utility function to add breadcrumb for user actions
 */
export const addSentryBreadcrumb = (message: string, category: string, data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data
  });
};